import React from 'react';
import { useTranslation } from 'react-i18next';
import { useWiberryRuns } from '../../hooks/useWiberryAgent';
import type { WiberryRun } from '../../services/wiberryAgentService';
import { formatDateTime, formatFreshness } from './wiberryFormat';
import './WiberryRunsList.css';
import './tableScroll.css';

export interface WiberryRunsListProps {
  /** Whether the integration has EVER synced, used only to suppress the
   * "never synced" negative claim when the runs feed is empty but we know
   * otherwise.
   *
   * Was `agents: WiberryAgentRow[]` (v1's `integration_agents` rows, checked
   * for `last_successful_sync_at`). S10 deleted that table's read route, and
   * the device flow never wrote to it anyway; the equivalent v2 signal is the
   * status endpoint's `last_sync`. */
  hasEverSynced?: boolean;
}

const MODE_LABELS: Record<WiberryRun['mode'], string> = {
  full: 'Vollständig',
  incremental: 'Inkrementell',
  reconcile: 'Abgleich',
};

const OUTCOME_LABELS: Record<WiberryRun['outcome'], string> = {
  running: 'Läuft',
  merged: 'Übernommen',
  failed: 'Fehlgeschlagen',
  expired: 'Abgelaufen',
};

/**
 * Plain-German cause for a failed run (FR-016.7: "a failed run shows its
 * cause in plain German, not a stack trace or error code"). The backend's
 * `error.code` maps here — `error.message` per protocol §3.7 is an
 * engineer-facing diagnostic string, not pre-translated prose, so it is
 * deliberately never rendered directly. Mirrors the `statusText()`
 * status→copy shape in `IntegrationAgentImports.tsx`.
 *
 * **Rewritten against the REAL vocabulary (#3120).** #3071 built this map
 * fixture-first, before any run row existed; not one of its four invented
 * codes (`connection_failed`/`schema_mismatch`/`auth_failed`/`timeout`) is
 * ever written by anything, so every genuinely failed run would have
 * rendered the generic fallback. The keys below are the codes actually
 * persisted to `integration_sync_runs.error`, grepped from their three
 * writers:
 *
 * - backend `wiberry_agent.py::complete_run` — `agent_reported_failure`
 * - worker `wiberry_sync_pipeline.py` — `get_run_failed`, `claim_failed`,
 *   `state_transition_failed`, `gate_failure`, `external_model_error`,
 *   `internal_error`, plus the `BatchContractError` codes it raises directly
 *   (`no_data`, `missing_batch_declaration`, `incomplete_batch_set`) and the
 *   ones `wiberry_sync_source.py` raises (`empty_batch`,
 *   `contract_violation`)
 * - worker `landing_pipeline.py` (#3234, the generic provider path) —
 *   `contract_drift`, `incomplete_landing_read`, `files_manifest_missing`,
 *   `transform_unavailable`, `registry_unreadable` (#3407), `pipeline_error`
 *
 * The generic fallback stays: these are three independently-evolving
 * writers, so an unmapped code is a normal event, not a bug.
 */
const FAILURE_COPY: Record<string, string> = {
  // The agent itself reported it could not complete (protocol §3.6
  // `outcome: "failed"`) — its own local cause never reaches us.
  agent_reported_failure: 'Der Agent auf Ihrem Rechner konnte den Sync-Lauf nicht abschließen.',
  // Batch-contract failures: what the agent uploaded was missing, incomplete
  // or unreadable. `no_data` is distinct from `empty_batch` — batches arrived
  // and parsed, but none carried an in-scope row.
  empty_batch: 'Der Agent hat keine Daten übertragen.',
  no_data: 'Der Sync-Lauf enthielt keine passenden Daten.',
  missing_batch_declaration: 'Die übertragenen Daten waren unvollständig.',
  incomplete_batch_set: 'Die übertragenen Daten waren unvollständig.',
  contract_violation: 'Die übertragenen Daten hatten ein unerwartetes Format.',
  // Schema/model drift on the wiberry side.
  contract_drift: 'Das wiberry-Datenbankschema hat sich geändert und konnte nicht verarbeitet werden.',
  external_model_error: 'Das wiberry-Datenbankschema hat sich geändert und konnte nicht verarbeitet werden.',
  gate_failure: 'Die übertragenen Daten haben die Qualitätsprüfung nicht bestanden.',
  // Staged data was incomplete when the compile step read it back.
  incomplete_landing_read: 'Die übertragenen Daten waren unvollständig.',
  files_manifest_missing: 'Die übertragenen Daten waren unvollständig.',
  // Server-side processing faults — nothing the farmer can act on.
  get_run_failed: 'Der Sync-Lauf konnte nicht verarbeitet werden.',
  claim_failed: 'Der Sync-Lauf konnte nicht verarbeitet werden.',
  state_transition_failed: 'Der Sync-Lauf konnte nicht verarbeitet werden.',
  transform_unavailable: 'Der Sync-Lauf konnte nicht verarbeitet werden.',
  registry_unreadable: 'Der Sync-Lauf konnte nicht verarbeitet werden.',
  pipeline_error: 'Der Sync-Lauf konnte nicht verarbeitet werden.',
  internal_error: 'Der Sync-Lauf konnte nicht verarbeitet werden.',
};

function failureText(run: WiberryRun): string {
  if (!run.error) return 'Der Sync-Lauf ist fehlgeschlagen.';
  return FAILURE_COPY[run.error.code] ?? 'Der Sync-Lauf ist fehlgeschlagen.';
}

/**
 * WiberryRunsList (#3071) — recent sync runs: start time, mode, outcome,
 * rows ingested, Erntejahre touched, plus the single freshness statement
 * ("Zuletzt aktualisiert: heute, 14:00 Uhr") derived from the latest
 * `merged` run.
 *
 * **Live since #3120.** #3071 shipped this section fixture-tested against a
 * path with no handler behind it (its backend deps hadn't landed); #3120
 * added the missing UI-facing handler at exactly that path, so the table
 * now renders real runs with no change to this component. `run.outcome` is
 * a server-side projection — the four in-flight UDB statuses arrive already
 * collapsed to `running`, which is why `OUTCOME_LABELS` has four members
 * and not seven.
 */
const WiberryRunsList: React.FC<WiberryRunsListProps> = ({ hasEverSynced = false }) => {
  const { t } = useTranslation();
  const { data, isLoading, error } = useWiberryRuns();
  const runs = data?.runs ?? [];

  const lastMergedRun = runs.find((run) => run.outcome === 'merged' && run.finished_at);
  const hasAnyAgentSynced = hasEverSynced;

  return (
    <div className="wiberry-runs-list">
      <h3 className="wiberry-runs-list__title">
        {t('integrations.wiberry.runs.title', 'Letzte Synchronisierungen')}
      </h3>

      {/* codex P2 (#3071): the "confirmed never synced" freshness line is a
          NEGATIVE CLAIM ("Noch keine erfolgreiche Synchronisierung"), so it
          must only render once the runs read has actually answered — while
          `isLoading`/`error`, an empty `runs` array means "we don't know
          yet", not "we checked and found none". */}
      {!isLoading && !error && (
        <>
          {lastMergedRun ? (
            <p className="wiberry-runs-list__freshness">
              {t('integrations.wiberry.runs.freshness', 'Zuletzt aktualisiert: {{when}}', {
                when: formatFreshness(lastMergedRun.finished_at),
              })}
            </p>
          ) : (
            !hasAnyAgentSynced && (
              <p className="wiberry-runs-list__freshness wiberry-runs-list__freshness--never">
                {t('integrations.wiberry.runs.neverSynced', 'Noch keine erfolgreiche Synchronisierung.')}
              </p>
            )
          )}
        </>
      )}

      {isLoading && (
        <div
          className="wiberry-runs-list__spinner"
          role="status"
          aria-label={t('integrations.wiberry.loading', 'Wird geladen…')}
        />
      )}

      {!isLoading && error && (
        <p className="wiberry-runs-list__error" role="alert">
          {t('integrations.wiberry.runs.error', 'Sync-Verlauf konnte nicht geladen werden.')}
        </p>
      )}

      {!isLoading && !error && runs.length === 0 && (
        <p className="wiberry-runs-list__empty">{t('integrations.wiberry.runs.empty', 'Noch keine Sync-Läufe.')}</p>
      )}

      {!isLoading && !error && runs.length > 0 && (
        <div
          className="integrations-table-scroll"
          role="region"
          aria-label={t('integrations.wiberry.runs.tableLabel', 'Sync-Läufe — Tabelle')}
          tabIndex={0}
        >
          <table className="wiberry-runs-list__table">
            <thead>
              <tr>
                <th scope="col">{t('integrations.wiberry.runs.columns.startedAt', 'Gestartet')}</th>
                <th scope="col">{t('integrations.wiberry.runs.columns.mode', 'Modus')}</th>
                <th scope="col">{t('integrations.wiberry.runs.columns.outcome', 'Ergebnis')}</th>
                <th scope="col">{t('integrations.wiberry.runs.columns.rows', 'Zeilen übernommen')}</th>
                <th scope="col">{t('integrations.wiberry.runs.columns.erntejahre', 'Erntejahre')}</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr key={run.id} className="wiberry-runs-list__row">
                  <td>{formatDateTime(run.started_at)}</td>
                  <td>{MODE_LABELS[run.mode] ?? run.mode}</td>
                  <td>
                    {OUTCOME_LABELS[run.outcome] ?? run.outcome}
                    {run.outcome === 'failed' && (
                      <p className="wiberry-runs-list__failure-reason">{failureText(run)}</p>
                    )}
                  </td>
                  <td>{run.rows_ingested ?? '—'}</td>
                  <td>{run.erntejahre && run.erntejahre.length > 0 ? run.erntejahre.join(', ') : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default WiberryRunsList;
